import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import {  faDownload, faUndo, faGlobePointer } from '@fortawesome/pro-solid-svg-icons';

import PageHeader from 'components/project/page_header';
import SelectFilter from 'components/search_sidebar/select_filter';
import SidebarSearch from 'components/search_sidebar/sidebar_search';
import LoadingSpinner from 'components/ui/loading_spinner';

import { get } from 'helpers/api';
import ActivityTable from './activity_table';
import Button from 'shared/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import Pagination from 'shared/pagination';
import axios from 'axios';
import { Col, Row } from 'react-bootstrap-5';

const ACTIVITIES_PER_PAGE = 50;

const ACTIVITY_TYPES = [
  { value: '', label: 'All User Activities' },
  { value: 'VaultActivities::EmailView', label: 'Opened Email' },
  { value: 'VaultActivities::PublicDocumentView', label: 'Viewed Public Documents' },
  { value: 'VaultActivities::CaViewed', label: 'Viewed CA' },
  { value: 'VaultActivities::CaSigned', label: 'Signed CA' },
  { value: 'VaultActivities::LockedDocumentView', label: 'Viewed Locked Documents' },
  { value: 'VaultActivities::DocumentDownload', label: 'Downloaded Documents' },
];

const SALE_OR_LEASE = [
  { value: '', label: 'Sale & Lease' },
  { value: 'sale', label: 'Sale' },
  { value: 'lease', label: 'Lease' }
];

const startingFilters = {
  searchText: '',
  activityType: ACTIVITY_TYPES[0].value,
  saleOrLease: SALE_OR_LEASE[0].value
};

const CONTENT_TO_SHOW = {
  LOADING: 'loading',
  EMPTY_STATE: 'empty_state',
  TABLE: 'table'
};

export function SharingActivityTab({
  activityCsvVaultsPath,
  features,
  vaultActivitiesPropertyPath,
  vaultActivitiesSvgPath
}) {
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState(startingFilters);
  const [loading, setLoading] = useState(true);
  const [vaultIds, setVaultIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const requestTimeoutId = useRef();

  useEffect(() => {
    if (!features.leadManagement) return;

    const abortController = new AbortController();
    fetchActivities(filters, currentPage, abortController);

    return () => abortController.abort();
  }, []);

  function fetchActivities(filtersForSearch = filters, offset = currentPage, abortController = null) {
    setLoading(true);
    const params = {
      name: filtersForSearch.searchText,
      type: filtersForSearch.activityType,
      saleOrLease: filtersForSearch.saleOrLease,
      offset
    };

    const data = { params: params };
    if (abortController) data.signal = abortController.signal;

    get(vaultActivitiesPropertyPath, data).then((data) => {
      setLoading(false);
      setVaultIds(data.vaultIds);
      setActivities(data.activities);
      setTotalActivities(data.totalActivities);
    }).catch((error) => {
      // eslint-disable-next-line no-console
      if (!axios.isCancel(error)) console.log(error);
    });
  }

  function handleFilterChange(filter, shouldDelay = false) {
    const updatedFilters = { ...filters, ...filter };
    setFilters(updatedFilters);
    setCurrentPage(1);

    clearTimeout(requestTimeoutId.current);
    if (shouldDelay) {
      requestTimeoutId.current = setTimeout(() => fetchActivities(updatedFilters, 1), 300);
    } else {
      fetchActivities(updatedFilters, 1);
    }
  }

  function updateCurrentPage(newPageNumber) {
    setCurrentPage(newPageNumber);
    fetchActivities(filters, newPageNumber);
  }

  function filtersAreEmpty() {
    Object.keys(filters).every(filterKey => filters[filterKey] === startingFilters[filterKey]);
  }

  const clearFilters = () => handleFilterChange(startingFilters);
  const handleSearchTextChange = e => handleFilterChange({ searchText: e.target.value || '' }, true);
  const handleActivityTypeChange = filter => handleFilterChange({ activityType: filter.value });
  const handleSaleOrLeaseChange = filter => handleFilterChange({ saleOrLease: filter.value });
  const currentActivityType = () => ACTIVITY_TYPES.find(activity => activity.value === filters.activityType);
  const currentSaleOrLease = () => SALE_OR_LEASE.find(saleOrLease => saleOrLease.value === filters.saleOrLease);

  function renderFilterTopbar() {
    return (
      <Row className="align-items-center gy-2">
        <Col className="d-flex gap-2 flex-wrap">
          <SidebarSearch
            name="filters"
            placeholder="Search..."
            value={filters.searchText}
            onChange={handleSearchTextChange}
          />
          <SelectFilter
            defaultValue={ACTIVITY_TYPES[0]}
            name="activity-type-filter"
            options={ACTIVITY_TYPES}
            value={currentActivityType()}
            onChange={handleActivityTypeChange}
          />
          {vaultIds.length > 1 && (
            <SelectFilter
              defaultValue={SALE_OR_LEASE[0]}
              name="sale-or-lease-filter"
              options={SALE_OR_LEASE}
              value={currentSaleOrLease()}
              onChange={handleSaleOrLeaseChange}
            />
          )}
        </Col>
        <Col lg="auto">
          <a className="btn btn-secondary" href={activityCsvVaultsPath}>
            <FontAwesomeIcon icon={faDownload}/>
            Download CSV
          </a>
        </Col>
      </Row>
    );
  }

  if (!loading && activities.length === 0 && filtersAreEmpty()) {
    return (
      <Empty className="bg-white mt-4">
        <Empty.Media>
          <FontAwesomeIcon icon={faGlobePointer} />
        </Empty.Media>
        <Empty.Title>No user activity has been recorded</Empty.Title>
      </Empty>
    );
  }

  const startingRange = ((currentPage - 1) * ACTIVITIES_PER_PAGE) + 1;
  const endingRange = Math.min(currentPage * ACTIVITIES_PER_PAGE, totalActivities);

  let contentToShow = CONTENT_TO_SHOW.TABLE;
  if (loading) {
    contentToShow = CONTENT_TO_SHOW.LOADING;
  } else if (activities.length === 0) {
    contentToShow = CONTENT_TO_SHOW.EMPTY_STATE;
  }

  return (
    <div>
      <PageHeader title="Web Activity"/>
      <div className="d-flex flex-column gap-2">
        {renderFilterTopbar()}
        {contentToShow === CONTENT_TO_SHOW.LOADING && (
          <div className="text-center my-5" name="loading-spinner">
            <LoadingSpinner />
          </div>
        )}
        {contentToShow === CONTENT_TO_SHOW.EMPTY_STATE && (
          <Empty className="bg-white">
            <Empty.Media>
              <FontAwesomeIcon icon={faExclamationCircle} />
            </Empty.Media>
            <Empty.Title>Unable to find Web Activity</Empty.Title>
            <Empty.Content>We were unable to find any Web Activity matching these filters</Empty.Content>
            <Empty.Actions>
              <Button.Primary icon={faUndo} label="Reset Filters" plain onClick={clearFilters} />
            </Empty.Actions>
          </Empty>
        )}
        {contentToShow === CONTENT_TO_SHOW.TABLE && (
          <React.Fragment>
            <div className="text-muted text-end small">
              Displaying {startingRange} - {endingRange} of {totalActivities} Web Activity
            </div>
            <ActivityTable activities={activities} vaultIds={vaultIds}/>
            <div className="d-flex justify-content-center mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalActivities / ACTIVITIES_PER_PAGE)}
                onChange={updateCurrentPage}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  features: state.features,
});

SharingActivityTab.propTypes = {
  activityCsvVaultsPath: PropTypes.string.isRequired,
  features: PropTypes.shape({
    projectTasksActivities: PropTypes.bool
  }).isRequired,
  vaultActivitiesPropertyPath: PropTypes.string.isRequired,
  vaultActivitiesSvgPath: PropTypes.string.isRequired
};

export default connect(mapStateToProps)(SharingActivityTab);
